import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, Navigate, matchPath } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box, Typography, IconButton, Tooltip, createTheme, CircularProgress } from '@mui/material';

import {
  Dashboard as DashboardIcon,
  Logout as LogoutIcon,
  EventNote,
  AddShoppingCart as AddShoppingCartIcon,
  ShoppingCartCheckout,
  BusinessCenter,
  AutoGraph,
  Summarize,
  AdminPanelSettings,
  Help,
  School,
  AddAlert,
} from '@mui/icons-material';

import { AppProvider } from '@toolpad/core/AppProvider';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import logo2 from '../../assets/images/wwm-logo-2.png';
import ReleaseNotes from '../ReleaseNotes/ReleaseNotes';
import SupportRequests from '../SupportRequests/SupportRequests';
import ApplicationMenuInternal from './ApplicationMenuInternal';
import ProjectTracker from '../ProjectTracker/ProjectTracker';
import VendorMgmt from '../VendorMgmt/VendorMgmt';
import Admin from '../Admin/Admin';
import POSummary from '../POSummary/POSummary';
import EstimateHistory from '../Estimator/EstimateHistory';
import ProjectAnalytics from '../ProjectAnalytics/ProjectAnalytics';
import ProcurementRequestPO from '../ProcurementRequest/ProcurementRequestPO';
import ProcurementRequestRFQ from '../ProcurementRequest/ProcurementRequestRFQ';
import HeaderStocks from '../../components/HeaderStocks/HeaderStocks';
import Tutorials from '../Tutorials/Tutorials';
import EditPricing from '../Estimator/EditPricing';
import EstimateManagement from '../Estimator/EstimateManagement';
import EstimatorApplicationMenuInternal from '../Estimator/EstimatorApplicationMenu';
import SparrowChat from '../Sparrow/SparrowChat';
import sparrowLogo from '../../assets/images/sparrow.png';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

import {
  ApplicationMenuPermissions,
  ProjectTrackerPermissions,
  ProcurementPOPermissions,
  ProcurementRFQPermissions,
  VendorMgmtPermissions,
  ProjectAnalyticsPermissions,
  POSummaryPermissions,
  PiplineEstimatorPermissions,
  AdminPermissions,
  TutorialPermissions,
  SparrowPermissions,
  ReleaseNotesPermissions,
} from '../../utils/permissions';
import NewEstimate from '../Estimator/NewEstimate';
import CalculateIcon from '@mui/icons-material/Calculate';

const NAVIGATION = [
  {
    segment: 'applicationmenu',
    title: 'Application Menu',
    icon: <DashboardIcon />,
    color: '#1976d2',
    permissions: ApplicationMenuPermissions,
  },
  {
    segment: 'projecttracker',
    title: 'Project Tracker & Updates',
    icon: <EventNote />,
    color: '#4caf50',
    permissions: ProjectTrackerPermissions,
  },
  {
    segment: 'procurementrequest/po',
    title: 'Procurement - Purchase Order',
    icon: <AddShoppingCartIcon />,
    color: '#ff5722',
    permissions: ProcurementPOPermissions,
  },
  {
    segment: 'procurementrequest/rfq',
    title: 'Procurement - Request For Quote',
    icon: <ShoppingCartCheckout />,
    color: '#ff9800',
    permissions: ProcurementRFQPermissions,
  },
  {
    segment: 'vendormgmt',
    title: 'Vendor Management',
    icon: <BusinessCenter />,
    color: '#673ab7',
    permissions: VendorMgmtPermissions,
  },
  {
    segment: 'estimator-menu',
    title: 'Pipeline Estimator',
    icon: <CalculateIcon />,
    color: '#673ab7',
    permissions: PiplineEstimatorPermissions,
  },
  {
    segment: 'projectanalytics',
    title: 'Project Analytics',
    icon: <AutoGraph />,
    color: '#009688',
    permissions: ProjectAnalyticsPermissions,
  },
  {
    segment: 'sparrow',
    title: 'Sparrow AI',
    icon: (
      <Box
        component="img"
        src={sparrowLogo}
        alt="Sparrow"
        sx={{
          width: 24,
          height: 24,
          objectFit: 'contain',
        }}
      />
    ),
    color: '#1976d2',
    permissions: SparrowPermissions,
  },

  {
    segment: 'posummary',
    title: 'PO Summary',
    icon: <Summarize />,
    color: '#607d8b',
    permissions: POSummaryPermissions,
  },
  { segment: 'admin', title: 'Admin', icon: <AdminPanelSettings />, color: '#f44336', permissions: AdminPermissions },
  {
    segment: 'tutorials',
    title: 'Tutorial Videos',
    icon: <School />,
    color: '#1976d2',
    permissions: TutorialPermissions,
  },
  {
    segment: 'supportrequests',
    title: 'Support Requests',
    icon: <Help />,
    color: '#000000',
    permissions: null,
  },
  {
    segment: 'releasenotes',
    title: 'Release Notes',
    icon: <AddAlert />,
    color: '#009688',
    permissions: ReleaseNotesPermissions,
  },
];

const demoTheme = createTheme({
  cssVariables: { colorSchemeSelector: 'data-toolpad-color-scheme' },
  colorSchemes: { light: true },
  breakpoints: { values: { xs: 0, sm: 600, md: 600, lg: 1200, xl: 1536 } },
});

function DemoPageContent({ navigate }) {
  const location = useLocation();
  const { userPermissions, isUnknownUser } = useAuth();

  const routes = {
    '/applicationmenu': {
      component: <ApplicationMenuInternal navigate={navigate} />,
      permissions: ApplicationMenuPermissions,
    },
    '/projecttracker': { component: <ProjectTracker />, permissions: ProjectTrackerPermissions },
    '/procurementrequest/po': { component: <ProcurementRequestPO />, permissions: ProcurementPOPermissions },
    '/procurementrequest/rfq': { component: <ProcurementRequestRFQ />, permissions: ProcurementRFQPermissions },
    '/vendormgmt': { component: <VendorMgmt />, permissions: VendorMgmtPermissions },
    '/estimator-menu': { component: <EstimatorApplicationMenuInternal />, permissions: VendorMgmtPermissions },
    '/new-estimate': { component: <NewEstimate />, permissions: VendorMgmtPermissions },
    '/edit-pipe-pricing': { component: <EditPricing />, permissions: VendorMgmtPermissions },
    '/estimate-management': { component: <EstimateManagement />, permissions: VendorMgmtPermissions },
    '/projectanalytics': { component: <ProjectAnalytics />, permissions: ProjectAnalyticsPermissions },
    '/posummary': { component: <POSummary />, permissions: POSummaryPermissions },
    '/sparrow': { component: <SparrowChat />, permissions: SparrowPermissions },
    '/admin': { component: <Admin />, permissions: AdminPermissions },
    '/estimate-history': { component: <EstimateHistory />, permissions: AdminPermissions },
    '/tutorials': { component: <Tutorials />, permissions: TutorialPermissions },
    '/supportrequests': { component: <SupportRequests />, permissions: null},
    '/releasenotes': { component: <ReleaseNotes />, permissions: ReleaseNotesPermissions },
    '/estimates/continue/:estimateId': { component: <NewEstimate />, permissions: AdminPermissions },
  };

  const routeKey = Object.keys(routes).find((path) =>
    matchPath(path, location.pathname)
  );
  const route = routeKey ? routes[routeKey] : null;

  // 1) Not a known route → send to application menu
  if (!route) {
    return <Navigate to="/applicationmenu" replace />;
  }

  // 2) Unknown or no-permission users → force into supportrequests
  if (isUnknownUser && location.pathname !== '/supportrequests') {
    return <Navigate to="/supportrequests" replace />;
  }

  // 3) Protected-route check
  if (
    Array.isArray(route.permissions) &&
    route.permissions.length > 0 &&
    !route.permissions.some((p) => userPermissions.includes(p))
  ) {
    return <Navigate to="/applicationmenu" replace />;
  }

  // 4) All good → render
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'center',
        overflowY: 'auto',
      }}
    >
      {route.component}
    </Box>
  );
}

DemoPageContent.propTypes = {
  navigate: PropTypes.func.isRequired,
  groupPermissions: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const SidebarIcon = ({ item, isActive, onClick }) => (
  <Tooltip title={item.title} placement="right">
    <IconButton
      onClick={onClick}
      sx={{
        color: isActive ? '#ffffff' : item.color,
        backgroundColor: isActive ? item.color : 'transparent',
        '&:hover': { backgroundColor: item.color, color: '#ffffff' },
      }}
    >
      {React.cloneElement(item.icon, { style: { color: isActive ? '#ffffff' : item.color } })}
    </IconButton>
  </Tooltip>
);

function SidebarFooter() {
  const handleSignOut = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/.auth/logout?post_logout_redirect_uri=/login';
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 0,
        width: '100%',
        textAlign: 'center',
        borderTop: '1px solid #e0e0e0',
        backgroundColor: '#f9f9f9',
        padding: '8px',
      }}
    >
      <Tooltip title="Sign Out" placement="right">
        <IconButton onClick={handleSignOut} sx={{ color: '#1976d2', '&:hover': { color: '#0d47a1' } }}>
          <LogoutIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function DashboardLayoutSidebarCollapsed() {
  const location = useLocation();
  const navigate = useNavigate();
  const { userPermissions, isUnknownUser, permsLoading } = useAuth();

  const userPermissionNames = useMemo(
    () => userPermissions,
     [userPermissions]
   );

  const visibleNavItems = useMemo(
    () =>
      NAVIGATION.filter((item) =>
        item.permissions
          ? item.permissions.some((p) => userPermissionNames.includes(p))
          : true
      ),
    [userPermissionNames]
  );

  if (permsLoading) {
    return (
     <Box sx={{display:'flex',justifyContent:'center',height:'80vh',alignItems:'center'}}>
        <CircularProgress/>
          <Typography variant="h6">Loading User Permissions</Typography>
      </Box>
    );
  }

  if (isUnknownUser && location.pathname !== '/supportrequests') {
    return <Navigate to="/supportrequests" replace />;
  }

  return (
    <AppProvider
      navigation={visibleNavItems}
      branding={{ logo: <img src={logo2} alt="WWM" />, title: '', homeUrl: '/toolpad/core/introduction' }}
      router={{ pathname: location.pathname, searchParams: new URLSearchParams(), navigate }}
      theme={demoTheme}
    >
      <DashboardLayout
        defaultSidebarCollapsed
        mini
        slots={{
          sidebarFooter: SidebarFooter,
          toolbarActions: HeaderStocks,
          sidebar: ({ collapsed }) => (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {collapsed &&
                visibleNavItems.map((item, index) =>
                  item.kind !== 'divider' ? (
                    <SidebarIcon
                      key={index}
                      item={item}
                      isActive={location.pathname.includes(item.segment)}
                      onClick={() => navigate(`/${item.segment}`)}
                    />
                  ) : null
                )}
            </Box>
          ),
        }}
      >
        <DemoPageContent navigate={navigate} groupPermissions={userPermissionNames} />
      </DashboardLayout>
    </AppProvider>
  );
}

export default DashboardLayoutSidebarCollapsed;
